import axios from 'axios';
import type {
  ReportListItem,
  ReportDefinitionResponse,
  ReportDefinitionRequest,
  GenerationLogListItem,
  ReportPreviewResponse,
  PageResponse,
} from '../types/reportBuilder';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

/** 바이너리 다운로드 전용 axios 인스턴스 (인터셉터 없이 blob 직접) */
function binaryGet(path: string): Promise<{ data: Blob; filename: string }> {
  const token = getStoredToken();
  return axios
    .get(`${import.meta.env.VITE_API_BASE ?? '/api/eraf'}${path}`, {
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    .then((res) => {
      const disposition = (res.headers['content-disposition'] as string) ?? '';
      const match = /filename[^;=\n]*=["']?([^"';\n]+)["']?/.exec(disposition);
      const filename = match?.[1]?.trim() ?? 'download';
      return { data: res.data as Blob, filename };
    });
}

/** POST generate — 바이너리 파일 반환 */
function binaryPost(
  path: string,
  params?: Record<string, string>
): Promise<{ data: Blob; filename: string }> {
  const token = getStoredToken();
  const sp = params ? '?' + new URLSearchParams(params).toString() : '';
  return axios
    .post(
      `${import.meta.env.VITE_API_BASE ?? '/api/eraf'}${path}${sp}`,
      undefined,
      {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    )
    .then((res) => {
      const disposition = (res.headers['content-disposition'] as string) ?? '';
      const match = /filename[^;=\n]*=["']?([^"';\n]+)["']?/.exec(disposition);
      const filename = match?.[1]?.trim() ?? 'report';
      return { data: res.data as Blob, filename };
    });
}

export const reportBuilderApi = {
  /** GET /report-builder/reports — 리포트 정의 목록 (REPORT_READ) */
  list: (params: {
    name?: string;
    dataSource?: string;
    status?: string;
    ownerScope?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<ReportListItem>> => {
    const sp = new URLSearchParams();
    if (params.name) sp.set('name', params.name);
    if (params.dataSource) sp.set('dataSource', params.dataSource);
    if (params.status) sp.set('status', params.status);
    if (params.ownerScope) sp.set('ownerScope', params.ownerScope);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<ReportListItem> }>(
        `/report-builder/reports?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** POST /report-builder/reports — 리포트 정의 생성 (REPORT_CREATE) */
  create: (body: ReportDefinitionRequest): Promise<ReportDefinitionResponse> =>
    apiClient
      .post<unknown, { data: ReportDefinitionResponse }>('/report-builder/reports', body)
      .then((res) => res.data),

  /** GET /report-builder/reports/{id} — 리포트 정의 상세 */
  get: (id: number): Promise<ReportDefinitionResponse> =>
    apiClient
      .get<unknown, { data: ReportDefinitionResponse }>(`/report-builder/reports/${id}`)
      .then((res) => res.data),

  /** PUT /report-builder/reports/{id} — 리포트 정의 수정 (REPORT_CREATE + owner) */
  update: (id: number, body: ReportDefinitionRequest): Promise<ReportDefinitionResponse> =>
    apiClient
      .put<unknown, { data: ReportDefinitionResponse }>(`/report-builder/reports/${id}`, body)
      .then((res) => res.data),

  /** DELETE /report-builder/reports/{id} — 리포트 정의 삭제 (REPORT_CREATE + owner) */
  remove: (id: number): Promise<void> =>
    apiClient
      .delete<unknown, { data: null }>(`/report-builder/reports/${id}`)
      .then(() => undefined),

  /**
   * POST /report-builder/reports/{id}/generate?format=... — 리포트 생성 (REPORT_GENERATE)
   * 바이너리(xlsx/pdf) 반환 → 브라우저 다운로드
   */
  generate: (
    id: number,
    format: 'excel' | 'pdf'
  ): Promise<{ data: Blob; filename: string }> =>
    binaryPost(`/report-builder/reports/${id}/generate`, { format }),

  /** GET /report-builder/reports/{id}/preview — 상위 N행 미리보기 (파일 생성 X) */
  preview: (id: number, rows = 50): Promise<ReportPreviewResponse> => {
    return apiClient
      .get<unknown, { data: ReportPreviewResponse }>(
        `/report-builder/reports/${id}/preview?rows=${rows}`
      )
      .then((res) => res.data);
  },

  /** GET /report-builder/generations — 생성 이력 목록 (REPORT_READ) */
  generationList: (params: {
    definitionId?: number;
    format?: string;
    status?: string;
    ownerScope?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<GenerationLogListItem>> => {
    const sp = new URLSearchParams();
    if (params.definitionId) sp.set('definitionId', String(params.definitionId));
    if (params.format) sp.set('format', params.format);
    if (params.status) sp.set('status', params.status);
    if (params.ownerScope) sp.set('ownerScope', params.ownerScope);
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<GenerationLogListItem> }>(
        `/report-builder/generations?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /report-builder/generations/{genId}/download — 이력 재다운로드 (REPORT_GENERATE)
   * 바이너리 반환 → 브라우저 다운로드
   */
  downloadGeneration: (genId: number): Promise<{ data: Blob; filename: string }> =>
    binaryGet(`/report-builder/generations/${genId}/download`),
};
